/*
  Landing page kit behaviour (added 24/09/2026).
  Loaded with defer by the LP sections that need it: lp-header (countdown),
  lp-quotes (carousel pager), lp-transformations (story carousel) and
  lp-buy-box (buy box), and lp-header again for <lp-cart-slot> when
  "Show the cart" is on. Custom elements re-initialise themselves
  when the theme editor re-renders a section, so there is no editor glue.
*/

/* <lp-countdown data-end="2026-10-01T23:59:00+10:00" data-expired="fallback|hide_bar">
   Ticks every second. When the end time passes it hides the live text and
   shows the fallback text, or hides the whole offer bar. The end time is
   an absolute ISO timestamp with its offset, so every visitor sees the same
   deadline wherever they are. data-end-ts (Unix seconds) takes precedence.
   data-weekly (20/10/2026): at the end it rolls on a week instead, so a
   weekly offer counts down to each Sunday midnight, and a cached page with
   last week's end still shows the right time. */
if (!customElements.get('lp-countdown')) {
  class LpCountdown extends HTMLElement {
    connectedCallback() {
      this.stop();
      const endTs = parseInt(this.dataset.endTs, 10);
      this.end = endTs > 0 ? endTs * 1000 : Date.parse(this.dataset.end || '');
      this.weekly = this.hasAttribute('data-weekly');
      this.parts = {
        dWrap: this.querySelector('[data-lp-days]'),
        d: this.querySelector('[data-lp-d]'),
        h: this.querySelector('[data-lp-h]'),
        m: this.querySelector('[data-lp-m]'),
        s: this.querySelector('[data-lp-s]')
      };
      if (Number.isNaN(this.end)) {
        this.expire();
        return;
      }
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      this.stop();
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    tick() {
      if (this.weekly) {
        while (this.end <= Date.now()) this.end += 7 * 86400000;
      }
      const left = this.end - Date.now();
      if (left <= 0) {
        this.expire();
        return;
      }
      const total = Math.floor(left / 1000);
      const days = Math.floor(total / 86400);
      const pad = (n) => String(n).padStart(2, '0');
      if (this.parts.d) this.parts.d.textContent = days;
      if (this.parts.dWrap) this.parts.dWrap.hidden = days === 0;
      if (this.parts.h) this.parts.h.textContent = pad(Math.floor((total % 86400) / 3600));
      if (this.parts.m) this.parts.m.textContent = pad(Math.floor((total % 3600) / 60));
      if (this.parts.s) this.parts.s.textContent = pad(total % 60);
    }

    expire() {
      this.stop();
      const bar = this.closest('[data-lp-offer]');
      if (!bar) return;
      if (this.dataset.expired === 'hide_bar') {
        bar.hidden = true;
        return;
      }
      const live = bar.querySelector('[data-lp-offer-live]');
      const fallback = bar.querySelector('[data-lp-offer-fallback]');
      if (live) live.hidden = true;
      if (fallback) {
        fallback.hidden = false;
      } else {
        bar.hidden = true;
      }
    }
  }
  customElements.define('lp-countdown', LpCountdown);
}

/* <lp-carousel> wraps a horizontal scroll-snap track ([data-lp-track]) and
   a pager ([data-lp-pager]: prev/next arrows plus a [data-lp-dots] holder).
   The pager only appears when the track actually overflows, with one dot per
   screenful of cards. The arrows move a screenful and wrap at either end,
   like <lp-story-carousel>. */
if (!customElements.get('lp-carousel')) {
  class LpCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lp-track]');
      this.dotsWrap = this.querySelector('[data-lp-dots]');
      if (!this.track || !this.dotsWrap) return;
      this.pager = this.querySelector('[data-lp-pager]') || this.dotsWrap;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.track.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }, { passive: true, signal: this.listeners.signal });
      this.addEventListener('click', (event) => {
        const control = event.target.closest('[data-lp-prev], [data-lp-next]');
        if (!control) return;
        this.goTo(this.current() + (control.hasAttribute('data-lp-prev') ? -1 : 1));
      }, options);
      this.resizeObserver = new ResizeObserver(() => this.build());
      this.resizeObserver.observe(this.track);
      this.build();
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    pageCount() {
      const width = this.track.clientWidth;
      if (!width) return 1;
      return Math.max(1, Math.round(this.track.scrollWidth / width));
    }

    current() {
      const pages = this.pageCount();
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      if (maxScroll > 0 && this.track.scrollLeft >= maxScroll - 2) return pages - 1;
      return Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
    }

    goTo(i) {
      const pages = this.pageCount();
      const page = ((i % pages) + pages) % pages;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: page * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    }

    build() {
      const pages = this.pageCount();
      this.dotsWrap.innerHTML = '';
      this.pager.hidden = pages < 2;
      if (pages < 2) return;
      const label = this.dataset.dotLabel || 'Go to slide';
      for (let i = 0; i < pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'lp-pager__dot';
        dot.setAttribute('aria-label', label + ' ' + (i + 1));
        dot.addEventListener('click', () => this.goTo(i));
        this.dotsWrap.appendChild(dot);
      }
      this.update();
    }

    update() {
      const dots = this.dotsWrap.querySelectorAll('button');
      if (!dots.length) return;
      const index = this.current();
      dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
    }
  }
  customElements.define('lp-carousel', LpCarousel);
}

/* <lp-story-carousel> drives the LP transformation stories cards while they
   scroll (below 990px): the arrows and dots in the cream pill, plus the
   optional auto-rotate. Ported from Transformation stories' <story-carousel>
   with two changes: it scrolls the track itself rather than calling
   scrollIntoView, which also scrolls the page when the section is off
   screen, and it only rotates while the cards actually overflow. */
if (!customElements.get('lp-story-carousel')) {
  class LpStoryCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lp-story-track]');
      this.cards = Array.from(this.querySelectorAll('[data-lp-story-card]'));
      this.dots = Array.from(this.querySelectorAll('[data-lp-story-dot]'));
      if (!this.track || this.cards.length < 2) return;
      this.index = 0;
      this.autorotate = this.dataset.autorotate === 'true';
      this.speed = parseInt(this.dataset.autorotateSpeed || '6000', 10);
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.addEventListener('click', (event) => {
        const control = event.target.closest('[data-lp-story-prev], [data-lp-story-next], [data-lp-story-dot]');
        if (!control) return;
        if (control.hasAttribute('data-lp-story-prev')) this.go(this.index - 1);
        else if (control.hasAttribute('data-lp-story-next')) this.go(this.index + 1);
        else this.go(Number(control.dataset.lpStoryDot));
      }, options);
      this.track.addEventListener('scroll', () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => this.sync(), 80);
      }, { passive: true, signal: this.listeners.signal });
      ['mouseenter', 'focusin'].forEach((type) => this.addEventListener(type, () => this.stopAuto(), options));
      ['mouseleave', 'focusout'].forEach((type) => this.addEventListener(type, () => this.startAuto(), options));
      this.startAuto();
    }

    disconnectedCallback() {
      this.stopAuto();
      window.clearTimeout(this.scrollTimer);
      if (this.listeners) this.listeners.abort();
    }

    overflows() {
      return this.track.scrollWidth > this.track.clientWidth + 1;
    }

    go(i) {
      const count = this.cards.length;
      this.index = ((i % count) + count) % count;
      const trackBox = this.track.getBoundingClientRect();
      const cardBox = this.cards[this.index].getBoundingClientRect();
      const offset = cardBox.left + cardBox.width / 2 - (trackBox.left + trackBox.width / 2);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollBy({ left: offset, behavior: reduce ? 'auto' : 'smooth' });
      this.updateDots();
    }

    sync() {
      const trackBox = this.track.getBoundingClientRect();
      const centre = trackBox.left + trackBox.width / 2;
      let closest = 0;
      let best = Infinity;
      this.cards.forEach((card, i) => {
        const box = card.getBoundingClientRect();
        const distance = Math.abs(box.left + box.width / 2 - centre);
        if (distance < best) {
          best = distance;
          closest = i;
        }
      });
      if (closest !== this.index) {
        this.index = closest;
        this.updateDots();
      }
    }

    updateDots() {
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
    }

    startAuto() {
      this.stopAuto();
      if (!this.autorotate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = window.setInterval(() => {
        if (this.overflows()) this.go(this.index + 1);
      }, this.speed);
    }

    stopAuto() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }
  customElements.define('lp-story-carousel', LpStoryCarousel);
}

/* <lp-buy-box> runs sections/lp-buy-box.liquid: the product page buy box on a
   landing page (25/09/2026). Same behaviour as the PDP engine in
   sections/main-product.liquid, kept in step with it:
   - Pack cards: the discount comes off each unit, rounded down to whole
     cents, then multiplies (Shopify's automatic discount maths).
   - Flavours (data-mode="flavours"): the shopper picks exactly as many as
     the chosen pack size. A smaller pack trims picks from the last tile.
     Each flavour is added as its own one-time line in one /cart/add.js
     request. Other products (data-mode="single") add the pack quantity of
     the product itself.
   - After adding: the cart drawer when it can be seen (the LP header
     usually hides the store header it lives in), otherwise the cart page,
     or straight to checkout if the section says so.
   Classic LP additions (14/10/2026), all inert unless the section asks:
   - One bundle (data-bundle): the button counts picks ("0/4"), then shows
     the price; one bar segment fills per pick.
   - Gallery arrows and an "n / total" counter (copy layouts).
   - Phone sticky bar ([data-lp-sticky]): shown below 750px whenever no
     buy box is on screen, from the top of the page (client, 20/10/2026:
     a button in the first fold; it used to wait until this box was
     passed). Its button takes the shopper to the box, or adds to cart
     once the flavours are picked.
   - Boxes for the same product on one page (data-sync-key) share their
     picks, so a hero and a closing offer never disagree. */
if (!customElements.get('lp-buy-box')) {
  class LpBuyBox extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.unitCents = parseInt(this.dataset.unitPrice, 10) || 0;
      this.currency = this.dataset.currency || 'AUD';
      this.flavourMode = this.dataset.mode === 'flavours';
      this.available = this.dataset.available !== 'false';
      this.atc = this.querySelector('[data-lp-atc]');
      this.atcLabel = this.querySelector('[data-lp-atc-label]');
      this.progress = this.querySelector('[data-lp-progress]');
      this.progressText = this.querySelector('[data-lp-progress-text]');
      this.errorEl = this.querySelector('[data-lp-error]');
      this.tiles = Array.from(this.querySelectorAll('[data-lp-flavour]'));
      this.mainImage = this.querySelector('[data-lp-main]');
      this.thumbRow = this.querySelector('[data-lp-thumbs]');
      this.thumbs = Array.from(this.querySelectorAll('[data-lp-thumb]'));
      this.bundle = this.dataset.bundle === 'true';
      this.atcMeta = this.querySelector('[data-lp-atc-meta]');
      this.pickedEl = this.querySelector('[data-lp-picked]');
      this.segs = Array.from(this.querySelectorAll('[data-lp-seg]'));
      this.readyEl = this.querySelector('[data-lp-ready]');
      this.readyList = this.querySelector('[data-lp-ready-list]');
      this.nipDialog = this.querySelector('[data-lp-nip]');
      this.counter = this.querySelector('[data-lp-counter]');
      this.sticky = this.querySelector('[data-lp-sticky]');
      this.stickyBtn = this.querySelector('[data-lp-sticky-btn]');
      this.stickyCount = this.querySelector('[data-lp-sticky-count]');
      this.target = this.packQty();
      this.wasComplete = null;
      this.listeners = new AbortController();
      const signal = this.listeners.signal;

      // All listeners share one AbortController, so a disconnect cleans up and
      // a reconnect starts fresh (14/10/2026)
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-lp-pack]')) this.onPackChange();
      }, { signal });
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      if (this.atc) this.atc.addEventListener('click', () => this.submit(), { signal });
      // Nutrition popup tabs: arrow keys move between flavours (16/10/2026)
      if (this.nipDialog) this.nipDialog.addEventListener('keydown', (event) => this.onNipKey(event), { signal });

      // Picks made in another box for the same product (14/10/2026)
      document.addEventListener('lp-buy:sync', (event) => this.onSync(event), { signal });

      this.refreshPrices();
      this.onPackChange();

      // Keep the pinned image column below the sticky LP review bar, if any
      this.onResize = () => this.setStickyTop();
      window.addEventListener('resize', this.onResize, { passive: true, signal });
      this.setStickyTop();
      if (this.sticky) this.setupSticky(signal);
    }

    disconnectedCallback() {
      if (this.listeners) this.listeners.abort();
      if (this.boxObserver) this.boxObserver.disconnect();
      // A re-rendered section in the editor reconnects a fresh element
      this.ready = false;
    }

    // Phone sticky bar: visible below 750px whenever no buy box is on screen,
    // from the top of the page (20/10/2026)
    setupSticky(signal) {
      this.visibleBoxes = new Set();
      this.boxesSeen = false;
      this.boxObserver = new IntersectionObserver((entries) => {
        this.boxesSeen = true;
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.visibleBoxes.add(entry.target);
          else this.visibleBoxes.delete(entry.target);
        });
        this.updateSticky();
      });
      // Watch the box itself (.lp-buy__details), not the whole element: in the
      // copy layouts the element also holds the copy column and gallery
      document.querySelectorAll('lp-buy-box').forEach((box) => {
        this.boxObserver.observe(box.querySelector('.lp-buy__details') || box);
      });
      this.phone = window.matchMedia('(max-width: 749px)');
      this.phone.addEventListener('change', () => this.updateSticky(), { signal });
      window.addEventListener('scroll', () => this.updateSticky(), { passive: true, signal });
      if (this.stickyBtn) {
        this.stickyBtn.addEventListener('click', () => {
          if (this.complete()) {
            this.submit();
          } else {
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const goal = this.querySelector('.lp-buy__flavours') || this;
            goal.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
          }
        }, { signal });
      }
      this.updateSticky();
    }

    updateSticky() {
      // Wait for the first observer report, so a page opened at a box never flashes the bar
      if (!this.sticky || !this.boxesSeen) return;
      const show = !!(this.phone && this.phone.matches) && this.visibleBoxes.size === 0;
      if (this.sticky.hidden === !show) return;
      this.sticky.hidden = !show;
      document.documentElement.classList.toggle('lp-has-sticky-buy', show);
    }

    // Apply picks made in another box for the same product and pack size
    onSync(event) {
      const detail = event.detail || {};
      if (detail.source === this || detail.key !== this.dataset.syncKey || detail.target !== this.target) return;
      this.tiles.forEach((tile) => {
        tile.dataset.count = detail.counts[tile.dataset.variant] || 0;
      });
      this.render(true);
    }

    broadcast() {
      if (!this.flavourMode || !this.dataset.syncKey) return;
      const counts = {};
      this.tiles.forEach((tile) => { counts[tile.dataset.variant] = parseInt(tile.dataset.count, 10) || 0; });
      document.dispatchEvent(new CustomEvent('lp-buy:sync', {
        detail: { source: this, key: this.dataset.syncKey, target: this.target, counts }
      }));
    }

    // Gallery arrows: step through the thumbnails, wrapping at either end
    stepGallery(delta) {
      if (!this.thumbs.length) return;
      let index = this.thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true');
      if (index < 0) index = 0;
      index = (index + delta + this.thumbs.length) % this.thumbs.length;
      this.showThumb(this.thumbs[index]);
    }

    setStickyTop() {
      const bar = document.querySelector('.lp-rating-bar');
      const section = bar && bar.closest('.shopify-section');
      const sticky = section && window.getComputedStyle(section).position === 'sticky';
      this.style.setProperty('--lp-buy-sticky-top', (sticky ? section.offsetHeight + 24 : 24) + 'px');
      // A #<Section ID> jump stops just below the sticky bar
      const root = this.closest('.lp-buy');
      if (root) root.style.setProperty('--lp-buy-anchor-offset', (sticky ? section.offsetHeight : 0) + 'px');
    }

    // Show a thumbnail's image large and mark it current. Scrolls only the
    // thumbnail row, never the page.
    showThumb(thumb) {
      if (!thumb || !this.mainImage) return;
      this.mainImage.srcset = thumb.dataset.srcset || '';
      this.mainImage.src = thumb.dataset.src;
      if (thumb.dataset.width) this.mainImage.width = parseInt(thumb.dataset.width, 10);
      if (thumb.dataset.height) this.mainImage.height = parseInt(thumb.dataset.height, 10);
      this.mainImage.alt = thumb.dataset.alt || '';
      this.thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = (this.thumbs.indexOf(thumb) + 1) + ' / ' + this.thumbs.length;
      if (this.thumbRow) {
        const row = this.thumbRow.getBoundingClientRect();
        const box = thumb.getBoundingClientRect();
        if (box.left < row.left || box.right > row.right) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.thumbRow.scrollBy({ left: box.left - row.left - 4, behavior: reduce ? 'auto' : 'smooth' });
        }
      }
    }

    money(cents, dropZeros) {
      const whole = dropZeros && cents % 100 === 0;
      try {
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2
        }).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(whole ? 0 : 2);
      }
    }

    checkedPack() {
      return this.querySelector('[data-lp-pack]:checked') || this.querySelector('[data-lp-pack]');
    }

    packQty() {
      const radio = this.checkedPack();
      return radio ? parseInt(radio.dataset.qty, 10) || 1 : 1;
    }

    calc(radio) {
      const qty = parseInt(radio.dataset.qty, 10) || 1;
      const discount = parseFloat(radio.dataset.discount) || 0;
      const unit = this.unitCents - Math.floor(this.unitCents * discount / 100);
      return { qty, unit, total: unit * qty, compare: this.unitCents * qty };
    }

    refreshPrices() {
      this.querySelectorAll('[data-lp-pack]').forEach((radio) => {
        const card = radio.closest('[data-lp-pack-card]');
        if (!card) return;
        const c = this.calc(radio);
        const unitEl = card.querySelector('[data-lp-unit]');
        const totalEl = card.querySelector('[data-lp-total]');
        const compareEl = card.querySelector('[data-lp-compare]');
        if (unitEl) unitEl.textContent = this.money(c.unit);
        if (totalEl) totalEl.textContent = this.money(c.total);
        if (compareEl) compareEl.textContent = this.money(c.compare);
      });
    }

    onPackChange() {
      this.querySelectorAll('[data-lp-pack-card]').forEach((card) => {
        const input = card.querySelector('[data-lp-pack]');
        card.classList.toggle('is-selected', !!(input && input.checked));
      });
      this.target = this.packQty();
      let over = this.picked() - this.target;
      if (over > 0) {
        this.tiles.slice().reverse().forEach((tile) => {
          if (over <= 0) return;
          const count = parseInt(tile.dataset.count, 10) || 0;
          const remove = Math.min(count, over);
          tile.dataset.count = count - remove;
          over -= remove;
        });
      }
      this.render();
    }

    picked() {
      return this.tiles.reduce((sum, tile) => sum + (parseInt(tile.dataset.count, 10) || 0), 0);
    }

    complete() {
      return !this.flavourMode || this.picked() === this.target;
    }

    flash() {
      if (!this.progress) return;
      this.progress.classList.add('is-attention');
      window.clearTimeout(this.flashTimer);
      this.flashTimer = window.setTimeout(() => this.progress.classList.remove('is-attention'), 1200);
    }

    onClick(event) {
      // Nutrition popup (16/10/2026): open, close (button or backdrop), tabs
      if (this.nipDialog) {
        if (event.target.closest('[data-lp-nip-open]')) { this.nipDialog.showModal(); return; }
        if (event.target.closest('[data-lp-nip-close]') || event.target === this.nipDialog) { this.nipDialog.close(); return; }
        const nipTab = event.target.closest('[data-lp-nip-tab]');
        if (nipTab) { this.showNipTab(nipTab); return; }
        if (this.nipDialog.contains(event.target)) return;
      }
      const thumb = event.target.closest('[data-lp-thumb]');
      if (thumb) {
        this.showThumb(thumb);
        return;
      }
      if (event.target.closest('[data-lp-gallery-prev]')) { this.stepGallery(-1); return; }
      if (event.target.closest('[data-lp-gallery-next]')) { this.stepGallery(1); return; }
      // Phone "Choose my 4 flavours" above the box (15/10/2026): glide down
      // to the flavours. The link's #id still works without JavaScript.
      if (event.target.closest('[data-lp-jump]')) {
        const goal = this.progress || this.querySelector('.lp-buy__flavours');
        if (goal) {
          event.preventDefault();
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          goal.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
        }
        return;
      }
      const tile = event.target.closest('[data-lp-flavour]');
      if (!tile || tile.dataset.available === 'false') return;
      const plus = event.target.closest('[data-lp-plus]');
      const minus = event.target.closest('[data-lp-minus]');
      let count = parseInt(tile.dataset.count, 10) || 0;
      if (plus || minus) {
        event.preventDefault();
        if (plus) {
          if (this.picked() >= this.target) { this.flash(); return; }
          count += 1;
        } else {
          count = Math.max(0, count - 1);
        }
      } else {
        // A click anywhere else on an empty tile adds one, as on the PDP
        if (count > 0) return;
        if (this.picked() >= this.target) { this.flash(); return; }
        count = 1;
      }
      const added = count > (parseInt(tile.dataset.count, 10) || 0);
      tile.dataset.count = count;
      this.render();
      this.broadcast();
      // Adding a flavour shows its pack shot, like picking a variant on a PDP
      if (added && tile.dataset.productId) {
        this.showThumb(this.thumbs.find((t) => t.dataset.productId === tile.dataset.productId));
      }
    }

    showNipTab(tab) {
      this.nipDialog.querySelectorAll('[data-lp-nip-tab]').forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }

    onNipKey(event) {
      const tab = event.target.closest('[data-lp-nip-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const tabs = Array.from(this.nipDialog.querySelectorAll('[data-lp-nip-tab]'));
      let i = tabs.indexOf(tab);
      if (event.key === 'ArrowLeft') i = (i - 1 + tabs.length) % tabs.length;
      if (event.key === 'ArrowRight') i = (i + 1) % tabs.length;
      if (event.key === 'Home') i = 0;
      if (event.key === 'End') i = tabs.length - 1;
      event.preventDefault();
      this.showNipTab(tabs[i]);
      tabs[i].focus();
      tabs[i].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    // synced: picks applied from another box, so don't pull this one into view
    render(synced) {
      const total = this.picked();
      const done = this.complete();
      const flavourWord = this.target === 1 ? 'flavour' : 'flavours';

      this.tiles.forEach((tile) => {
        const count = parseInt(tile.dataset.count, 10) || 0;
        const plus = tile.querySelector('[data-lp-plus]');
        const minus = tile.querySelector('[data-lp-minus]');
        const countEl = tile.querySelector('[data-lp-count]');
        const badge = tile.querySelector('[data-lp-badge]');
        tile.classList.toggle('is-picked', count > 0);
        tile.classList.toggle('is-empty', count === 0);
        tile.classList.toggle('is-limit-reached', total >= this.target && count === 0);
        if (countEl) countEl.textContent = count;
        if (badge) badge.textContent = count;
        if (minus) minus.disabled = count === 0;
        if (plus) {
          plus.disabled = total >= this.target;
          plus.textContent = count === 0 ? '+ Add' : '+';
        }
      });

      if (this.progressText) {
        this.progressText.textContent = done
          ? 'Your ' + this.target + ' ' + flavourWord + (this.target === 1 ? ' is' : ' are') + ' selected'
          : 'Choose your ' + this.target + ' ' + flavourWord + ' · ' + total + '/' + this.target + ' selected';
      }
      if (this.progress) this.progress.classList.toggle('is-complete', done);

      if (this.atc && this.available) {
        this.atc.setAttribute('aria-disabled', done ? 'false' : 'true');
        if (this.bundle) {
          if (this.atcLabel) this.atcLabel.textContent = done ? this.dataset.addLabel : this.dataset.chooseLabel;
          if (this.atcMeta) this.atcMeta.textContent = done ? this.money(this.bundleTotal(), true) : total + '/' + this.target;
          this.atc.classList.toggle('is-ready', done);
        } else if (this.atcLabel) {
          this.atcLabel.textContent = done ? 'Add to cart' : 'Select flavours';
        }
      }

      // One bundle: pick count and one segment per pick
      if (this.pickedEl) this.pickedEl.textContent = total;
      this.segs.forEach((seg, i) => seg.classList.toggle('is-filled', i < total));
      // "Your bundle is ready" with the picks, as the design (16/10/2026)
      if (this.readyEl) {
        this.readyEl.hidden = !done;
        if (done && this.readyList) {
          this.readyList.textContent = this.tiles
            .filter((tile) => (parseInt(tile.dataset.count, 10) || 0) > 0)
            .map((tile) => {
              const name = tile.querySelector('.lp-buy__flavour-name');
              return tile.dataset.count + '× ' + (name ? name.textContent.trim() : '');
            })
            .join(' · ');
        }
      }
      if (this.stickyCount) {
        this.stickyCount.hidden = total === 0 || done;
        this.stickyCount.textContent = '· ' + total + ' of ' + this.target;
      }
      if (this.stickyBtn && this.flavourMode) {
        this.stickyBtn.textContent = done ? 'Add to cart' : (this.stickyChooseLabel || (this.stickyChooseLabel = this.stickyBtn.textContent));
      }

      // Bring the button into view the moment the picks are complete (not on load)
      if (this.flavourMode && this.wasComplete === false && done && this.atc && !synced) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.setTimeout(() => this.atc.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }), 180);
      }
      this.wasComplete = done;
    }

    bundleTotal() {
      const radio = this.checkedPack();
      return radio ? this.calc(radio).total : this.unitCents;
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      window.clearTimeout(this.errorTimer);
      this.errorTimer = window.setTimeout(() => { this.errorEl.hidden = true; }, 6000);
    }

    // The drawer counts only if nothing above it is display: none (the LP
    // header hides the header group the drawer is rendered in).
    usableDrawer() {
      const drawer = document.querySelector('mini-cart');
      if (!drawer || typeof drawer.renderContents !== 'function') return null;
      for (let el = drawer.parentElement; el && el !== document.body; el = el.parentElement) {
        if (window.getComputedStyle(el).display === 'none') return null;
      }
      return drawer;
    }

    lineItems() {
      if (!this.flavourMode) {
        const id = parseInt(this.dataset.variantId, 10);
        return id ? [{ id, quantity: this.packQty() }] : [];
      }
      const items = [];
      this.tiles.forEach((tile) => {
        const count = parseInt(tile.dataset.count, 10) || 0;
        const id = parseInt(tile.dataset.variant, 10);
        if (count > 0 && id) items.push({ id, quantity: count });
      });
      return items;
    }

    submit() {
      if (!this.available || this.atc.getAttribute('aria-busy') === 'true') return;
      if (!this.complete()) {
        this.flash();
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (this.progress) this.progress.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      const items = this.lineItems();
      if (!items.length) return;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const afterAdd = this.dataset.afterAdd || 'drawer';
      const drawer = afterAdd === 'drawer' ? this.usableDrawer() : null;
      const payload = { items };
      if (drawer) {
        payload.sections = drawer.getSectionsToRender().map((s) => s.id).join(',');
        payload.sections_url = window.location.pathname;
        if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(this.atc);
      }

      this.atc.setAttribute('aria-busy', 'true');
      if (this.errorEl) this.errorEl.hidden = true;

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then((response) => response.json().then((json) => {
          if (!response.ok) throw new Error(json.description || json.message || 'Something went wrong. Please try again.');
          return json;
        }))
        .then((json) => {
          this.atc.removeAttribute('aria-busy');
          if (afterAdd === 'checkout') {
            window.location.href = root + 'checkout';
          } else if (drawer) {
            drawer.renderContents(json);
          } else {
            window.location.href = root + 'cart';
          }
        })
        .catch((error) => {
          this.atc.removeAttribute('aria-busy');
          this.flash();
          this.showError(error.message);
        });
    }
  }
  customElements.define('lp-buy-box', LpBuyBox);
}

/* <lp-cart-slot> sits on the right of the LP header's logo bar when "Show
   the cart" is on. The LP header hides the store header, and the store's
   cart drawer lives inside it, so this moves the store's own <cart-drawer>
   (button, total, count and drawer) in here. Moving keeps everything the
   theme already does: the same ids, so section re-renders after an add
   still find them, and the same listeners, set up once in the element's
   constructor. Without a drawer (drawer off in theme settings) it moves the
   plain cart link instead. */
if (!customElements.get('lp-cart-slot')) {
  class LpCartSlot extends HTMLElement {
    connectedCallback() {
      if (this.childElementCount) return;
      const cart = document.querySelector('cart-drawer') ||
        document.querySelector('.shopify-section-group-header-group a#cart-icon-bubble');
      if (cart && !this.contains(cart)) this.appendChild(cart);
    }
  }
  customElements.define('lp-cart-slot', LpCartSlot);
}
